<!--
  //app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchStatsPlayerStats.vue
  -->

<script setup lang="ts">
import type { Team } from "#shared/team/domain/models/Team";
import type { Parameter } from "#shared/stats/domain/models/Parameter";
import type { Player } from "#shared/player/domain/Player";
import type {
  MatchStatsPlayer,
  MatchStatsPlayerData,
  MatchStatsTeam,
} from "#shared/stats/domain/models/MatchStats";
import type { SeasonStatsPartner } from "#shared/season-cluster/domain/models/01.Season";
import playerService from "#shared/player/domain/services/playerService";
import matchStatsUiService from "#shared/stats/infrastructure/ui-services/matchStatsUiService";
import ChevronLeftIcon from "~/components/Shared/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "~/components/Shared/icons/ChevronRightIcon.vue";
import PlayerStatsModal from "~/components/pages/region-matches-match-homeTeam-vs-awayTeam/PlayerStatsModal.vue";
import FeaturedPlayerLightningIcon from "~/components/Shared/icons/FeaturedPlayerLightningIcon.vue";

type Props = {
  homeTeamInfo: Team;
  awayTeamInfo: Team;
  homeTeamStatsData: MatchStatsTeam;
  awayTeamStatsData: MatchStatsTeam;
  parameters: Parameter[];
  seasonId: number;
  mvpPartner?: SeasonStatsPartner;
};

const props = defineProps<Props>();

const { playerIsVisible } = playerService();
const { filterMatchPlayerStats } = matchStatsUiService();

const homeVisiblePlayers = props.homeTeamInfo.players.filter(p => playerIsVisible(p) && !!p.category);
const awayVisiblePlayers = props.awayTeamInfo.players.filter(p => playerIsVisible(p) && !!p.category);

const selectedPlayer = ref<Player>();
const selectedPlayerTeam = ref<Team>();
const filteredPlayerStats = ref<MatchStatsPlayerData[]>();

const openPlayerModal = (player: Player | undefined, team: Team | undefined) => {
  selectedPlayer.value = player;
  selectedPlayerTeam.value = team;

  const homePlayers = props.homeTeamStatsData ? props.homeTeamStatsData.players || [] : [];
  const awayPlayers = props.awayTeamStatsData ? props.awayTeamStatsData.players || [] : [];

  const players = [...homePlayers, ...awayPlayers] || [];
  const playerStatsData = players.find((p: MatchStatsPlayer) => p.playerId === player?.id);

  if (playerStatsData?.playerStats && player?.role) {
    filteredPlayerStats.value = filterMatchPlayerStats(
      props.parameters,
      playerStatsData.playerStats,
      player.role
    );
    document.body.classList.add("overflow-hidden");
  }
};

const closePlayerModal = () => {
  selectedPlayer.value = undefined;
  document.body.classList.remove("overflow-hidden");
};
</script>

<template>
  <div class="flex flex-row gap-16">
    <!--            Home Players STATS-->

    <div class="basis-1/2">
      <ul>
        <li
          v-for="player in homeVisiblePlayers"
          class="player-row"
          :key="player.id"
          @click="openPlayerModal(player, homeTeamInfo)"
        >
          <div class="flex justify-between">
            <div class="flex gap-2 items-center">
              <!-- 11, 12, 13 category player icon -->
              <featured-player-lightning-icon
                v-if="player && player.category && player.category !== 'draft'"
                :width="20"
                :height="20"
                icon-color="white"
                circle-color="text-secondary"
              ></featured-player-lightning-icon>
              <div class="flex flex-col gap-2 text-left sm:flex-row">
                <span v-if="player.jersey || player.jersey === 0" class="text-xs text-primary font-bold">
                  #{{ player.jersey.toString() }}
                </span>
                <span class="player-row--name player-row--name-home text-xs text-gray-200">
                  {{ player.shortName }}
                </span>
                <span v-if="player.role" class="text-xs text-gray-600 capitalize">
                  {{ $t(player.role) }}
                </span>
              </div>
            </div>

            <div class="player-row--btn player-row--btn-home">
              <span class="text-xs text-gray-600 hidden md:block">
                {{ $t("general.stats.title") }}
              </span>

              <chevron-right-icon class="player-row--btn-icon player-row--btn-icon-home rtl:rotate-180" />
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!--            Away Players STATS-->

    <div class="basis-1/2">
      <ul>
        <li
          v-for="player in awayVisiblePlayers"
          class="player-row"
          :key="player.id"
          @click="openPlayerModal(player, awayTeamInfo)"
        >
          <div class="flex justify-between">
            <div class="player-row--btn player-row--btn-away">
              <chevron-left-icon class="player-row--btn-icon player-row--btn-icon-away rtl:rotate-180" />

              <span class="text-xs text-gray-600 hidden md:block">
                {{ $t("general.stats.title") }}
              </span>
            </div>

            <div class="flex gap-2 items-center">
              <div class="flex flex-col gap-2 text-right sm:flex-row">
                <span v-if="player.jersey || player.jersey === 0" class="text-xs text-primary font-bold">
                  #{{ player.jersey.toString() }}
                </span>
                <span class="text-xs text-gray-200">
                  {{ player.shortName }}
                </span>
                <span
                  v-if="player.role"
                  class="player-row--name player-row--name-away text-xs text-gray-600 capitalize"
                >
                  {{ $t(player.role) }}
                </span>
              </div>
              <!-- 11, 12, 13 category player icon -->
              <featured-player-lightning-icon
                v-if="player && player.category && player.category !== 'draft'"
                :width="20"
                :height="20"
                icon-color="white"
                circle-color="text-secondary"
              ></featured-player-lightning-icon>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>

  <div
    v-if="homeTeamInfo.players.length <= 0 || awayTeamInfo.players.length <= 0"
    class="py-3 px-4 rounded-lg bg-gray-800"
  >
    <p class="text-xs lg:text-sm text-gray-400">{{ $t("match.stats.players.list.empty") }}</p>
  </div>

  <player-stats-modal
    v-if="selectedPlayer && selectedPlayerTeam && filteredPlayerStats"
    :player="selectedPlayer"
    :team="selectedPlayerTeam"
    :playerStatsData="filteredPlayerStats"
    :seasonId="seasonId"
    :player-image="selectedPlayer.image"
    @close="closePlayerModal"
    :mvp-partner="mvpPartner"
  ></player-stats-modal>
</template>

<style scoped>
.player-row {
  @apply border-b border-gray-700 py-2.5 hover:cursor-pointer;
}

.player-row--btn-icon {
  @apply h-4 w-4 md:w-0 text-gray-600;
}

.player-row--btn {
  @apply flex items-center;
}

.player-row--btn-home,
.player-row--name-home,
.player-row--btn-icon-home {
  @apply transition-all ease-in-out duration-200;
}

.player-row--btn-away,
.player-row--name-away,
.player-row--btn-icon-away {
  @apply transition-all ease-in-out duration-200;
}

.player-row:hover .player-row--btn > span {
  @apply text-primary;
}

.player-row:hover .player-row--name {
  @apply md:ms-1.5;
}

.player-row:hover .player-row--btn-icon {
  @apply w-4;
}
</style>
